import axios from 'axios';
import i18next from 'i18next';
import { BACKGROUND_READ_TIMEOUT_MS } from './readTimeout';

/**
 * One in-flight GET /api/users/me/branches per scope (PERF-API-01 P03).
 *
 * Opening a job card cold mounts the shell's BranchSelector and the card's own
 * transfer-eligibility check at the same moment, and both asked the server for
 * the same authorized branch list. This coalesces the concurrent pair into one
 * request and hands both callers the same answer.
 *
 * DELIBERATELY NOT A CACHE. Nothing is retained once the request settles: the
 * next caller after that gets a fresh authorized read. A branch grant revoked
 * between two page opens is therefore still reflected on the next open, which
 * is the behaviour before this change — the endpoint stays the single source of
 * truth for what the user may switch into, and no result outlives the request
 * that produced it.
 *
 * The scope key exists for the same reason: a token or active-branch change
 * while a read is outstanding starts a new request instead of adopting the
 * previous scope's promise. Callers receive their own array copy so one screen
 * cannot mutate another's list.
 */

let inFlight = null;
let inFlightScope = null;

/**
 * The staff-facing label for a branch: its name (Arabic in an Arabic UI), then
 * the short branch reference after a dash when it adds something — e.g.
 * "Autofix Maintenance Center — Main". The name is what the branch is called
 * everywhere else (QC read a reference-only list as "the name changed"); the
 * reference still tells same-named branches apart. Share this everywhere a
 * staff list names a branch (owner rule 2026-10-14).
 */
export function branchLabel(b) {
  const name = (i18next.language === 'ar' && b?.name_ar) || b?.name || '';
  const ref = String(b?.reference || '').trim();
  if (!ref || ref === name) return name || ref;
  return name ? `${name} — ${ref}` : ref;
}

function currentScopeKey() {
  let token = '';
  let activeGarageId = '';
  try {
    token = localStorage.getItem('token') || '';
    activeGarageId = localStorage.getItem('activeGarageId') || '';
  } catch (e) {
    // Private-mode/storage-denied browsers: fall back to the headers alone.
  }
  const headers = axios.defaults.headers.common || {};
  return [
    token,
    activeGarageId,
    headers['x-garage-id'] || '',
    headers['x-garage-scope'] || '',
  ].join('|');
}

export function fetchAuthorizedBranches({ singleBranch = false } = {}) {
  const scope = currentScopeKey() + (singleBranch ? '|single' : '');
  if (!inFlight || inFlightScope !== scope) {
    inFlightScope = scope;
    // Bounded (B02). This promise is SHARED: while it is pending every later
    // caller in the same scope adopts it, so a read that never settles would
    // hand a permanently pending promise to the branch selector and to every
    // transfer check after it. A timeout rejects, the slot is released below,
    // and the next open issues a fresh authorized read.
    const config = { timeout: BACKGROUND_READ_TIMEOUT_MS };
    if (singleBranch) config.__singleBranch = true;
    const request = axios.get('/api/users/me/branches', config).then((res) => res.data?.data || []);
    inFlight = request;
    const release = () => {
      if (inFlight === request) {
        inFlight = null;
        inFlightScope = null;
      }
    };
    request.then(release, release);
  }
  return inFlight.then((list) => list.slice());
}

// Tests only: drop any pending sharing so one case cannot leak into the next.
export function __resetAuthorizedBranches() {
  inFlight = null;
  inFlightScope = null;
}

/**
 * The branch picker's switch: persist the branch under the key the axios
 * request interceptor reads (index.js → 'activeGarageId'), remember it as the
 * All-branches fallback, and set the in-memory header for any request that
 * fires before the caller reloads. BranchSelector and the notification bell
 * both switch through this one function; each caller then reloads.
 */
export function applyActiveBranch(branchId) {
  axios.defaults.headers.common['x-garage-id'] = branchId;
  delete axios.defaults.headers.common['x-garage-scope'];
  localStorage.setItem('activeGarageId', branchId);
  localStorage.setItem('lastRealGarageId', branchId);
}

/**
 * Switch into All-branches mode: set the request header and persist activeGarageId = 'all'.
 * Leaves lastRealGarageId untouched so writes in All-scope can still fall back to it.
 */
export function applyAllBranches() {
  axios.defaults.headers.common['x-garage-scope'] = 'all';
  try {
    localStorage.setItem('activeGarageId', 'all');
  } catch (e) {
    // Storage unavailable
  }
}

/**
 * Persist the user's latest branch choice ('all' or a branch id) for pre-selection on sign-in.
 * No-op when userId is falsy.
 */
export function rememberBranchChoice(userId, choice) {
  if (!userId) return;
  try {
    localStorage.setItem('mm.lastBranchChoice', JSON.stringify({ userId, choice }));
  } catch (e) {
    // Storage unavailable
  }
}

/**
 * The last stored branch choice for this user, or null if missing, belongs to
 * another user, or has corrupt JSON.
 */
export function lastBranchChoice(userId) {
  if (!userId) return null;
  try {
    const raw = localStorage.getItem('mm.lastBranchChoice');
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && parsed.userId === userId) {
      return parsed.choice ?? null;
    }
    return null;
  } catch (e) {
    return null;
  }
}

/**
 * The branch single-document reads run on: the picked branch, or in
 * All-branches mode the concrete fallback the interceptor sends as x-garage-id.
 */
export function effectiveBranchId() {
  const active = localStorage.getItem('activeGarageId');
  return active && active !== 'all' ? active : localStorage.getItem('lastRealGarageId');
}
